export const SET_SCHEDULE = 'SET_SCHEDULE';
export const TOGGLE_STAGE = 'TOGGLE_STAGE';
export const TOGGLE_CATEGORY = 'TOGGLE_CATEGORY';
export const SET_QUANTITY = 'SET_QUANTITY';
export const SET_SCREEN = 'SET_SCREEN';

// Screen types
export const SCHEDULE_SELECT_SCREEN = 'SCHEDULE_SELECT_SCREEN';
export const STAGE_SELECT_SCREEN = 'STAGE_SELECT_SCREEN';
export const STAGE_DISPLAY_SCREEN = 'STAGE_DISPLAY_SCREEN';
export const CALCULATION_SCREEN = 'CALCULATION_SCREEN';

// Fee types
export const FIXED = 'Fixed';
export const HALF_HOURLY = 'Half-Hourly';
export const ESTIMATE_REQUIRED = 'Estimate required';

export const initialState = {
  schedules: null,
  stages: [],
  activeSchedule: null,
  activeStages: [],
  activeCategories: [],
  calculation: 0,
  screen: {
    type: SCHEDULE_SELECT_SCREEN,
    index: 0,
    scroll: false,
  },
  errorState: false,
  disabledCategories: [], // array of IDs
}

export default function reducer(state: object, action) {
  const newState = { ...state };
  const { type, payload } = action;

  switch (type) {
    case SET_SCHEDULE: { // payload is ID
      // Match schedule based on ID
      if (payload) {
        // This creates a deep clone of the schedule object, this addresses issues with data persisting through a state reset.
        newState.activeSchedule = JSON.parse(JSON.stringify(state.schedules.find(value => value.id === payload)));

        // Set available stages based on schedule selected
        newState.stages = newState.activeSchedule.stages;
      } else {
        // This represents using the reset button
        newState.activeSchedule = null;
        newState.screen.type = SCHEDULE_SELECT_SCREEN;
        newState.screen.index = 0;
      }

      // clear active stages and categories
      newState.activeStages = [];
      newState.activeCategories = [];

      break;
    }
    case TOGGLE_STAGE: { // payload is ID
      // if stage is already active then remove, else add to list
      if (state.activeStages.find(value => value.id === payload )) {
        let stageToRemove = state.activeStages.find(value => value.id === payload);
        let categoriesToRemove = stageToRemove.subStages.map((obj, index) => {
          return obj.categories;
        }).flat();

        // Remove any active categories for this stage
        newState.activeCategories = state.activeCategories.filter(n => !categoriesToRemove.includes(n));

        // Remove the stage
        newState.activeStages = state.activeStages.filter(stage => stage.id !== payload);
      } else {
        newState.activeStages.push(state.stages.find(value => value.id === payload));
        newState.activeStages = newState.activeStages.sort(function (a, b) { return a.sort - b.sort });
      }
      break;
    }
    case TOGGLE_CATEGORY: { // payload is ID
      // if category is already active then remove, else add to list
      if (state.activeCategories.length && state.activeCategories.find(value => value.categoryID === payload)) {
        newState.activeCategories = state.activeCategories.filter(category => category.categoryID !== payload);
      } else {
        // This is possibly an unnecessary level of complexity
        let category = null;
        state.activeStages.find((value, index) => {
          value.subStages.find((value, index) => {
            value.categories.find((value, index) => {
              if (value.categoryID === payload) {
                category = value;
              }
            })
          })
        });
        if (category) {
          newState.activeCategories.push(category);
        };
      }
      break;
    }
    case SET_QUANTITY: { // payload is { ID, quantity }
      state.activeStages.find((value, index) => {
        value.subStages.find((value, index) => {
          value.categories.find((value, index) => {
            if (value.categoryID === payload.ID) {
              value.feeQuantity = payload.quantity;

              // make category active when inputting a value
              if (payload.quantity > 0 && !newState.activeCategories.find(category => category.categoryID === payload.ID)) {
                newState.activeCategories.push(value);
              }
            }
          })
        })
      });

      break;
    }
    case SET_SCREEN: { // payload is ( 1 / -1 ), indicating forward or back
      switch (payload) {
        case (1):
          newState.screen.scroll = true;
          break;
        case (-1):
          newState.screen.scroll = false;
          break;
      }
      switch (state.screen.type) {
        case (SCHEDULE_SELECT_SCREEN):
          newState.screen.type = STAGE_SELECT_SCREEN;
          break;
        case (STAGE_SELECT_SCREEN):
          if (payload < 0) {
            newState.screen.type = SCHEDULE_SELECT_SCREEN;
          } else {
            newState.screen.type = STAGE_DISPLAY_SCREEN;
          }
          break;
        case (STAGE_DISPLAY_SCREEN):
          if (state.screen.index >= state.activeStages.length - 1 && payload > 0) {
            newState.screen.type = CALCULATION_SCREEN;
          } else if (payload < 0 && state.screen.index === 0) {
            newState.screen.type = STAGE_SELECT_SCREEN;
          } else {
            newState.screen.index = state.screen.index + payload;
          }
          break;
        case (CALCULATION_SCREEN):
          if (payload < 0) {
            newState.screen.index = state.activeStages.length - 1;
            newState.screen.type = STAGE_DISPLAY_SCREEN;
          }
          break;
        default: {
          throw Error('Unknown screen: ' + state.screen.type);
        }
      }
      break;
    }
    default: {
      throw Error('Unknown action: ' + type);
    }
  }

  // Error checking
  newState.errorState = false;
  if (newState.activeStages.length) {
    let stage = state.activeStages[state.screen.index];

    stage.subStages.map((obj, index) => {
      obj.categories.map((value, index) => {
        if (value.maxQuantity !== 1) {
          value.error = value.error || {};
          if (!value.feeQuantity && newState.activeCategories.includes(value)) {
            value.error.message = "Quantity is required";
            value.error.isError = true;
            newState.errorState = true;
          } else if (value.feeQuantity < 0) {
            value.error.message = "Negative values are not permitted";
            value.error.isError = true;
            newState.errorState = true;
          } else if (value.feeQuantity % 1 > 0) {
            value.error.message = "Decimal values are not permitted";
            value.error.isError = true;
            newState.errorState = true;
          } else if (value.maxQuantity > 0 && value.feeQuantity > value.maxQuantity) {
            value.error.message = "Exceeds maximum allowed quantity";
            value.error.isError = true;
            newState.errorState = true;
          } else {
            value.error.message = '';
            value.error.isError = false;
          }
        }
        return value;
      })
    })
  }

  //  Manage disabled states for mutually exclusive fees
  newState.disabledCategories = [];
  if (newState.activeStages.length) {
    newState.activeCategories.map((obj, index) => {
      if (obj.exclusiveCategories.length) {
        obj.exclusiveCategories.map((el, index) => {
          newState.disabledCategories.push(el);
        })
      }
    })

    let stage = newState.activeStages[state.screen.index];
    stage.subStages.map((obj, index) => {
      obj.categories.map((value, index) => {
        newState.disabledCategories.includes(value.categoryID)
          ? value.isDisabled = true
          : value.isDisabled = false
      })
    })
  }

  // Calculate total
  newState.calculation = 0;
  newState.activeCategories.map((category, index) => {
    if (category.maxQuantity === 1) {
      newState.calculation += category.feeValue;
    } else {
      newState.calculation += category.feeValue * (category.feeQuantity ?? 0);
    }
  });

  return newState;
}
